/**
 * Blog post: telc C1 Prüfung – Aufbau, Zeiten, Punkte, Hochschule vs. Allgemein.
 * SEO target (GSC, German searchers): "telc c1", "telc c1 prüfung",
 * "telc c1 hochschule", "format c1". Facts match the /mock-exams/telc-c1 page.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function TelcC1ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;
  const link = 'font-bold text-black underline underline-offset-2 hover:opacity-80';

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>Von Lehrern entwickelt, abgeglichen mit dem offiziellen telc-Prüfungsaufbau.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Die <strong>telc C1 Prüfung</strong> hat fünf Teile, dauert mit Mündlichem knapp dreieinhalb Stunden
        und wird nur bestanden, wenn der schriftliche und der mündliche Teil jeweils mindestens 60 % erreichen.
        Dieser Leitfaden zeigt dir den Aufbau von <strong>telc Deutsch C1 Hochschule</strong> mit Zeiten und
        Punkten, erklärt den Unterschied zu <strong>telc C1 Allgemein</strong> und sagt dir, womit du
        dich am besten vorbereitest.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Aufbau der telc C1 Hochschule Prüfung
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Die Prüfung besteht aus einem schriftlichen Teil (166 Punkte) und einem mündlichen Teil (48 Punkte),
        zusammen 214 Punkte:
      </p>
      <div className="overflow-x-auto my-6 rounded-2xl border-2 border-black/10 bg-white shadow-sm">
        <table className="w-full min-w-[480px] text-left text-sm">
          <thead>
            <tr className="border-b-2 border-black/10 bg-black/5">
              <th className="px-4 py-3 font-space font-bold text-black">Prüfungsteil</th>
              <th className="px-4 py-3 font-space font-bold text-black">Zeit</th>
              <th className="px-4 py-3 font-space font-bold text-black">Punkte</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Leseverstehen</td>
              <td className="px-4 py-3 text-black/70">90 Min. (gemeinsam mit Sprachbausteine)</td>
              <td className="px-4 py-3 text-black/70">48</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Sprachbausteine</td>
              <td className="px-4 py-3 text-black/70">gemeinsame Zeit mit Leseverstehen</td>
              <td className="px-4 py-3 text-black/70">22</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Hörverstehen</td>
              <td className="px-4 py-3 text-black/70">40 Min.</td>
              <td className="px-4 py-3 text-black/70">48</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Schriftlicher Ausdruck</td>
              <td className="px-4 py-3 text-black/70">70 Min.</td>
              <td className="px-4 py-3 text-black/70">48</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Mündlicher Ausdruck</td>
              <td className="px-4 py-3 text-black/70">16 Min.</td>
              <td className="px-4 py-3 text-black/70">48</td>
            </tr>
            <tr className="bg-black/[0.03]">
              <td className="px-4 py-3 font-bold text-black">Gesamt</td>
              <td className="px-4 py-3 font-bold text-black">3 Std. 36 Min.</td>
              <td className="px-4 py-3 font-bold text-black">214</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Wie viele Punkte brauchst du zum Bestehen?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Die Bestehensgrenze liegt bei <strong>60 %</strong>, und zwar getrennt für beide Teile:
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-6 ml-2">
        <li><strong>Schriftlich:</strong> 60 % von 166 Punkten, also rund 100 Punkte.</li>
        <li><strong>Mündlich:</strong> 60 % von 48 Punkten, also rund 29 Punkte.</li>
      </ul>
      <div className="my-6 p-4 sm:p-5 rounded-2xl border-l-4 border-amber-500 bg-amber-50/80">
        <p className="text-base md:text-lg text-black/80">
          <strong>Wichtig:</strong> Ein starker mündlicher Teil gleicht einen schwachen schriftlichen Teil nicht
          aus. Beide Teile müssen für sich bestanden werden. Der <strong>Schriftliche Ausdruck</strong> bringt
          allein 48 der 166 schriftlichen Punkte und ist für viele Prüflinge die größte Hürde.
        </p>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        telc C1 Hochschule oder telc C1 Allgemein?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Beide Prüfungen liegen auf dem Niveau C1. Entscheidend ist, wofür du das Zertifikat brauchst:
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-6 ml-2">
        <li>
          <strong>telc C1 Hochschule</strong> ist für Studienbewerber gedacht. Die Texte und Aufgaben
          stammen aus dem akademischen Alltag, im Schriftlichen Ausdruck schreibst du eine strukturierte
          Stellungnahme von mindestens 350 Wörtern.
        </li>
        <li>
          <strong>telc C1 Allgemein</strong> ist für Beruf und Alltag gedacht, mit anderen Themen und
          Aufgabentypen.
        </li>
      </ul>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Ob eine Hochschule das Zertifikat für die Zulassung akzeptiert, legt die Hochschule selbst fest.
        Lies deshalb vor der Anmeldung die Zulassungsordnung deines Studiengangs oder frag das
        International Office.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        So bereitest du dich vor
      </h2>
      <ol className="list-decimal list-inside space-y-3 text-base md:text-lg text-black/70 leading-relaxed mb-6 ml-2">
        <li>
          <strong>Mach einen kompletten Modelltest unter Zeitdruck.</strong> Der
          {' '}<Link to={`/${lang}/mock-exams/telc-c1`} className={link}>telc C1 Hochschule Modelltest</Link>{' '}
          folgt dem offiziellen Aufbau mit allen fünf Teilen, und du siehst am Ende zu jeder Aufgabe die richtige Antwort.
        </li>
        <li>
          <strong>Trainiere den Schriftlichen Ausdruck gezielt.</strong> Struktur, Aufbau und Musterabsätze
          findest du im {' '}<Link to={`${blogBase}/telc-c1-schreiben-guide`} className={link}>telc C1 Schreiben Guide</Link>.
        </li>
        <li>
          <strong>Sammle Redemittel.</strong> Die
          {' '}<Link to={`${blogBase}/telc-c1-redemittel-schreiben`} className={link}>telc C1 Redemittel</Link>{' '}
          gibt es kostenlos als PDF zum Ausdrucken.
        </li>
        <li>
          <strong>Übe das Sprechen laut.</strong> Die Musterdialoge unten zeigen, wie die mündliche Prüfung abläuft.
        </li>
      </ol>
      <SprechenSamplesCallout provider="telc" level="c1" />

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-[#1e293b] text-white">
        <h2 className="font-space text-xl font-bold text-white mb-3">
          Teste dich mit einem kostenlosen telc C1 Modelltest
        </h2>
        <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
          Lesen, Sprachbausteine, Hören und Schreiben mit echter Prüfungszeit, Punkten pro Teil und einer
          Erklärung zu jeder Antwort. Der erste Modelltest ist kostenlos.
        </p>
        <Link
          to={`/${lang}/mock-exams/telc-c1`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-space font-bold rounded-xl border-2 border-emerald-500 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Zum telc C1 Modelltest
        </Link>
      </div>

      <RelatedResources provider="telc" level="c1" variant="exam-guide" />

    </article>
  );
}
